import React from 'react';
import { X, Clock, CheckCircle } from 'lucide-react';

const ScheduledMessagesTracker = ({
  isOpen,
  onClose,
  scheduledMessages,
  onCancelMessage
}) => {
  if (!isOpen) return null;
  
  const pendingMessages = scheduledMessages.filter(m => m.status === 'pending');
  
  if (pendingMessages.length === 0 && !isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-black border border-purple-500/50 rounded-xl shadow-[0_0_30px_rgba(168,85,247,0.3)] overflow-hidden">
      <div className="px-4 py-3 bg-purple-600/20 border-b border-purple-500/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-purple-400" />
          <h3 className="text-sm font-bold text-white">Scheduled Messages</h3>
          <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded-full">
            {pendingMessages.length}
          </span>
        </div>
        <button 
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <X size={16} className="text-gray-400" />
        </button>
      </div>
      
      <div className="max-h-64 overflow-y-auto">
        {pendingMessages.map((msg, idx) => {
          const scheduledTime = new Date(msg.scheduledFor);
          const now = new Date();
          const timeDiff = scheduledTime - now;
          const isExpired = timeDiff <= 0;
          
          // Calculate countdown
          const hours = Math.floor(timeDiff / (1000 * 60 * 60));
          const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
          
          return (
            <div key={msg.id || idx} className="p-3 border-b border-gray-800 last:border-b-0 hover:bg-white/5">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{msg.to}</p>
                  <p className="text-xs text-gray-400 truncate">{msg.message?.substring(0, 50)}{msg.message?.length > 50 ? '...' : ''}</p>
                </div>
                <button 
                  onClick={() => onCancelMessage(msg.id)}
                  className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                  title="Cancel scheduled message"
                >
                  <X size={14} />
                </button>
              </div>
              
              {/* Countdown Timer */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-gray-500">
                  {scheduledTime.toLocaleString()}
                </p>
                {isExpired ? (
                  <span className="px-2 py-1 bg-green-600/20 text-green-400 text-[10px] font-semibold rounded flex items-center gap-1">
                    <CheckCircle size={10} />
                    Sending...
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-purple-600/20 text-purple-300 text-[10px] font-mono font-semibold rounded">
                    {hours > 0 ? `${hours}h ` : ''}{minutes}m {seconds}s
                  </span>
                )}
              </div>
              
              {/* Progress Bar */}
              <div className="mt-2 w-full bg-gray-800 rounded-full h-1 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${isExpired ? 'bg-green-500' : 'bg-purple-500'}`}
                  style={{ 
                    width: isExpired ? '100%' : `${Math.max(0, 100 - (timeDiff / (1000 * 60 * 60 * 24)) * 100)}%`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      
      {pendingMessages.length === 0 && (
        <div className="p-4 text-center">
          <CheckCircle size={24} className="mx-auto mb-2 text-green-400" />
          <p className="text-sm text-gray-400">All messages sent!</p>
        </div>
      )}
    </div>
  );
};

export default ScheduledMessagesTracker;
